/**
 * Converts and compresses an image file to a Base64 string using Canvas.
 * Resizes the image to a maximum width of 800px and reduces quality to 70%.
 */
const fileToBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 800;
            let width = img.width;
            let height = img.height;

            // Maintain aspect ratio
            if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to JPEG to optimize LocalStorage usage
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
    };
});

/**
 * Persists recipe data to LocalStorage.
 * Handles image processing and data normalization.
 */
export const saveRecipe = async (recipeData) => {
    try {
        const localData = localStorage.getItem('recipes_book');
        const recipes = localData ? JSON.parse(localData) : [];

        let mainImageUrl = "";
        if (recipeData.coverFile) {
            mainImageUrl = await fileToBase64(recipeData.coverFile);
        }

        const filesToProcess = recipeData.galleryFiles || [];
        const permanentGallery = await Promise.all(
            filesToProcess.map(file => fileToBase64(file))
        );

        const newRecipe = {
            _id: crypto.randomUUID(),
            name: recipeData.name,
            price: Number(recipeData.price),
            ingredients: recipeData.ingredients,
            preparation_steps: recipeData.steps.map((s, i) => ({
                step: i + 1,
                description: s.description
            })),
            gallery: permanentGallery,
            image_url: mainImageUrl,
            created_at: new Date().toISOString()
        };

        recipes.push(newRecipe);
        localStorage.setItem('recipes_book', JSON.stringify(recipes));

        return { success: true };
    } catch (error) {
        console.error("LocalStorage Save Error:", error);
        return { success: false, error: error.message };
    }
};


export const getRecipeListLocalService = () => {
    try {
        const data = localStorage.getItem('recipes_book');
        return data ? JSON.parse(data) : [];
    } catch (error) {
        console.error("Error leyendo LocalStorage:", error);
        return [];
    }
};

/**
 * Deletes a recipe from LocalStorage by its ID.
 */
export const deleteRecipe = (id) => {
    try {
        const data = localStorage.getItem('recipes_book');
        if (!data) return { success: false, error: "No hay recetas guardadas." };

        const recipes = JSON.parse(data);

        // Filtramos para mantener solo las recetas que NO coincidan con el ID
        const updatedRecipes = recipes.filter(recipe => recipe._id !== id);

        // Si miden lo mismo, significa que no se encontró ninguna receta con ese ID
        if (recipes.length === updatedRecipes.length) {
            return { success: false, error: "Receta no encontrada." };
        }

        // Guardamos la nueva lista en LocalStorage
        localStorage.setItem('recipes_book', JSON.stringify(updatedRecipes));
        return { success: true };
    } catch (error) {
        console.error("Error eliminando receta de LocalStorage:", error);
        return { success: false, error: error.message };
    }
};